import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { MatDialogConfig } from '@angular/material/dialog'
import { AlertService } from '@app/alert/service/alert.service'
import { ModalService } from '@app/modal/modal.service'
import { ArquivoModel } from '@shared/models/backend'
import { FileDownloaderService } from '../file-downloader/file-downloader.service'
import { DEFAULT_EXTENSIONS, DEFAULT_MAX_SIZE } from './file-uploader-default-configs'
import { FileUploaderService } from './file-uploader.service'

@Component({
  selector: 'app-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileUploaderComponent implements OnInit {
  @Input()
  title: string

  @Input()
  maxSize = DEFAULT_MAX_SIZE

  @Input()
  accept = DEFAULT_EXTENSIONS

  @Input()
  required = true

  @Input()
  disabled: boolean

  @Input()
  formGroup: FormGroup

  @Input()
  control: FormControl

  validExtensions: string[]
  public isUploading = false

  constructor(
    private alertService: AlertService,
    private uploaderService: FileUploaderService,
    private ref: ChangeDetectorRef,
    private modalService: ModalService,
    private fileDownloaderService: FileDownloaderService
  ) {}

  ngOnInit(): void {
    this.validExtensions = this.accept.replace(/\s/g, '').split(',')
    this.formGroup.statusChanges.subscribe((_) => this.ref.markForCheck())
  }

  get arquivo() {
    return this.control?.value as ArquivoModel
  }

  get filename() {
    return this.arquivo?.nome ? this.truncate(this.arquivo?.nome, 30) : ''
  }

  get errorMessage() {
    return this.control?.errors ? Object.values(this.control.errors)[0] : null
  }

  get hasError() {
    return (
      !this.isUploading &&
      this.control?.parent?.invalid &&
      (this.control?.dirty || this.control?.touched) &&
      this.errorMessage
    )
  }

  download() {
    this.fileDownloaderService.download(this.arquivo)
  }

  private truncate(text, len) {
    if (!text) {
      return text
    }
    const ext = text.substring(text.lastIndexOf('.') + 1, text.length).toLowerCase()
    const filename = text.replace('.' + ext, '')
    if (filename.length <= len) {
      return text
    }
    return filename.substr(0, len).trim() + (text.length > len ? '...' : '') + '.' + ext
  }

  onClick(fileInput: HTMLInputElement) {
    this.formGroup.markAsDirty() // TODO avaliar métodos alternativos para essa situação
    this.control.setValue(null)
    this.control.markAsTouched()
    fileInput.value = ''
    fileInput.click()
  }

  async onChange(event: Event) {
    const files = (event.target as HTMLInputElement).files
    if (files?.length > 0) {
      const file = files[0]
      if (file.size > this.maxSize * 1024 * 1024) {
        this.notifyFileSizeProblem()
      } else if (
        !this.validExtensions.some((ext) => file?.name?.toLocaleLowerCase()?.endsWith(ext?.toLocaleLowerCase()))
      ) {
        this.notifyFileExtensionProblem()
      } else {
        await this.syncFile(file)
      }
    }
    this.ref.markForCheck()
  }

  removeFile() {
    const confirm = this.modalService.openConfirmModal(this.confirmRemoveModalConfig())
    confirm.afterClosed().subscribe((result) => {
      if (result) {
        this.formGroup.markAsDirty() // TODO avaliar métodos alternativos para essa situação
        this.ref.markForCheck()
        this.control.setValue(null)
        this.notifyFileRemoved()
      }
    })
  }

  private notifyFileSizeProblem() {
    this.alertService.showError('Apenas arquivos de até ' + this.maxSize + 'MB são permitidos', this.title)
  }

  private notifyFileExtensionProblem() {
    this.alertService.showError('Apenas arquivos com extensão ' + this.accept + ' são permitidos', this.title)
  }

  private notifyFileUploadProblem() {
    this.alertService.showError('Não foi possível enviar o arquivo', this.title)
  }

  private notifyFileUploaded() {
    this.alertService.showSuccess('Arquivo anexado com sucesso', this.title)
  }

  private notifyFileRemoved() {
    this.alertService.showSuccess('Arquivo excluído com sucesso', this.title)
  }

  private async syncFile(file: File) {
    this.isUploading = true
    this.ref.markForCheck()
    try {
      const arquivo = await this.uploaderService.upload(file).toPromise()
      this.control.setValue(arquivo)
      this.notifyFileUploaded()
    } catch (err) {
      this.notifyFileUploadProblem()
    }
    this.isUploading = false
  }

  private confirmRemoveModalConfig() {
    const dialogConfig = new MatDialogConfig()
    dialogConfig.width = '24em'
    dialogConfig.data = {
      title: 'Deseja excluir o anexo ' + this.title + '?',
      primaryOption: 'EXCLUIR',
      secondaryOption: 'CANCELAR',
      message: 'Atenção! Não será possível desfazer esta ação.',
    }

    return dialogConfig
  }
}
